// src/components/LegalViewPage/LegalViewPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import styles from "../../gmanewstyle.module.css";
import logoimage from "../assets/Group -2.png";
import PortalSidebar from "../PortalSidebar/PortalSidebar";

/* ────────── tiny paginated footer ────────── */
const Pagination = ({ page, total, perPage, onChange }) => {
  const first = (page - 1) * perPage + 1;
  const last = Math.min(first + perPage - 1, total);
  const pages = Math.ceil(total / perPage);
  return (
    <div className={styles.pagination}>
      <button
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
      >
        Previous
      </button>
      <span className={styles.pageMeta}>
        {first}-{last} of {total}
      </span>
      <button
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        onClick={() => onChange(page + 1)}
        disabled={page === pages}
      >
        Next
      </button>
    </div>
  );
};

function transformNoteHtml(htmlString) {
  if (!htmlString) return "";
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");

  doc.querySelectorAll("a").forEach((a) => {
    const txt = (a.textContent || "").trim();
    if (/\.(eml|msg)$/i.test(txt)) {
      const span = doc.createElement("span");
      span.textContent = txt;
      span.className = a.className;
      a.replaceWith(span);
    }
  });

  return doc.body.innerHTML;
}

export default function ContractorLegalViewPage() {
  const { id, contractorId } = useParams();
  const navigate = useNavigate();
  const [legal, setLegal] = useState(null);
  const [payments, setPayments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aodFile, setAodFile] = useState(null);
  const [aodMeta, setAodMeta] = useState({ exists: false, file_name: null });
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [displayName, setDisplayName] = useState("Name Surname ▾");
  const [conductRulesDocs, setConductRulesDocs] = useState([]);
  const [conductRulesLoading, setConductRulesLoading] = useState(false);
  const [conductRulesError, setConductRulesError] = useState("");
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsLoaded, setPaymentsLoaded] = useState(false);
  const [paymentsError, setPaymentsError] = useState("");

  const [notesLoading, setNotesLoading] = useState(false);
  const [notesLoaded, setNotesLoaded] = useState(false);
  const [notesError, setNotesError] = useState("");

  const [conductRulesLoaded, setConductRulesLoaded] = useState(false);
  const [stmtFormat, setStmtFormat] = useState("pdf"); // pdf | excel
  const [stmtDelivery, setStmtDelivery] = useState("download"); // download | email
  const [attorneyRef, setAttorneyRef] = useState("");
  const [savingAttorneyRef, setSavingAttorneyRef] = useState(false);

  // ── NEW: timeline-note input & posting state
  const [newTimelineNote, setNewTimelineNote] = useState("");
  const [postingNote, setPostingNote] = useState(false);

  /* ────────── helpers ────────── */
  const display = (obj, key) =>
    obj?.[`${key}@OData.Community.Display.V1.FormattedValue`] ??
    obj?.[key] ??
    "—";

  // NEW: date formatter (day-month-year only)
  const fmtDate = (value) => {
    if (!value) return "";

    const d = new Date(value);

    if (isNaN(d.getTime()) || d.getTime() === 0 || d.getFullYear() <= 1970) {
      return "";
    }

    return format(d, "dd MMM yyyy");
  };

  // NEW: detect if a value looks like a date
  const isDateish = (v) => {
    if (!v) return false;
    const d = new Date(v);
    return !isNaN(d.getTime());
  };

  const formatAmount = (n) => {
    if (n == null || isNaN(n)) return "—";
    const [int, dec] = Number(n).toFixed(2).split(".");
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, " ") + "," + dec;
  };

  const formatAgeTotal = (value) => {
    const amount = Number(value);

    if (value === null || value === undefined || Number.isNaN(amount)) {
      return "—";
    }

    return `R ${formatAmount(amount)}`;
  };

  const getAgeTotalClass = (value) => {
    if (value === null || value === undefined) return "";

    const amount = Number(value);
    if (Number.isNaN(amount) || amount === 0) return "";

    return amount < 0 ? styles.ageTotalCredit : styles.ageTotalDebit;
  };

  const [userAccess, setUserAccess] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(true);

  // ────────── NEW: edit mode + payload state for the 3 boxes ──────────
  const [isEditing, setIsEditing] = useState(false);
  const [savingEdits, setSavingEdits] = useState(false);
  const [saveError, setSaveError] = useState("");

  // friendly-key payload that matches the backend update API
  const [editFields, setEditFields] = useState({
    // case events
    letter_of_demand: "",
    summons_sent_to_court: "",
    summons_served: "",
    warrant_of_execution: "",
    judgement: "", // "Yes" | "No" | ""
    judgement_granted: "",
    section65: "",
    section66: "",
    // aod
    aod_start_date: "",
    aod_end_date: "",
    aod_amount: "",
    // current situation
    current_situation: "",
    nulla_bonna: "",
    attachment: "",
    rule46_application: "",
  });
  const [originalEditFields, setOriginalEditFields] = useState({});

  // ────────── NEW: Load Payment form state ──────────
  const [paymentForm, setPaymentForm] = useState({
    file: null,
    amount: "",
    invoice: "",
    invoiceDate: "",
  });

  const loadAsInvoice = legal?.requires_legal_invoice === true;
  const [uploadingPayment, setUploadingPayment] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState(""); // success/info
  const [paymentError, setPaymentError] = useState(""); // error text

  const toYMD = (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (isNaN(d)) return "";
    return d.toISOString().slice(0, 10);
  };

  const initEditFieldsFromLegal = (leg) => {
    const judgeFmt =
      leg?.["cr446_judgement@OData.Community.Display.V1.FormattedValue"];
    const judgeBool =
      typeof leg?.cr446_judgement === "boolean"
        ? leg.cr446_judgement
          ? "Yes"
          : "No"
        : "";

    const nullaFmt =
      leg?.["cr446_nullabonna@OData.Community.Display.V1.FormattedValue"];
    const nullaBool =
      typeof leg?.cr446_nullabonna === "boolean"
        ? leg.cr446_nullabonna
          ? "Yes"
          : "No"
        : "";

    const attachmentFmt =
      leg?.["cr446_attachment@OData.Community.Display.V1.FormattedValue"];
    const attachmentBool =
      typeof leg?.cr446_attachment === "boolean"
        ? leg.cr446_attachment
          ? "Yes"
          : "No"
        : "";
    const activeFmt =
      leg?.[
        "cr446_activeattorneyaod@OData.Community.Display.V1.FormattedValue"
      ];
    const activeBool =
      typeof leg?.cr446_activeattorneyaod === "boolean"
        ? leg.cr446_activeattorneyaod
          ? "Yes"
          : "No"
        : "";

    const fields = {
      // case events
      letter_of_demand: toYMD(leg?.cr446_letterofdemand),
      summons_sent_to_court: toYMD(leg?.cr446_summonssenttocourt),
      summons_served: toYMD(leg?.cj_summonsserved),
      warrant_of_execution: toYMD(leg?.cr446_warrantofexecution),
      judgement: judgeFmt || judgeBool || "",
      judgement_granted: toYMD(leg?.cj_judgementgranted),
      section65: toYMD(leg?.cj_section65),
      section66: toYMD(leg?.cj_section66),
      // aod
      aod_start_date: toYMD(leg?.cj_attorneyaodstartdate),
      aod_end_date: toYMD(leg?.cj_aodenddate),
      aod_amount: leg?.cj_aodamount ?? "",
      active_attorney_aod: activeFmt || activeBool || "",
      // current situation
      current_situation: leg?.cj_currentsituationdescription ?? "",
      nulla_bonna: nullaFmt || nullaBool || "",
      attachment: attachmentFmt || attachmentBool || "",
      rule46_application: toYMD(leg?.cr446_rule46application),
    };
    setEditFields(fields);
    setOriginalEditFields(fields);
  };
  const API_BASE = "https://portal.gmacc.co.za/api";
  //const API_BASE = "http://127.0.0.1:8000/api";
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Default sidebar state based on device width
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);

      // Only force closed when moving to smaller screens.
      // On desktop, keep whatever state the user last chose.
      if (!desktop) {
        setSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    function handleClick(e) {
      // On desktop/laptop, only the burger should control the sidebar
      if (isDesktop) return;

      const sidebar = document.getElementsByClassName(styles.sidebar)[0];
      const hamburger = document.getElementsByClassName(styles.menuBtn)[0];

      if (!sidebar || !hamburger) return;

      const clickedInsideSidebar = sidebar.contains(e.target);
      const clickedHamburger = hamburger.contains(e.target);

      if (sidebarOpen && !clickedInsideSidebar && !clickedHamburger) {
        setSidebarOpen(false);
      }
    }

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [sidebarOpen, isDesktop]);

  // ─── Determine access + user display name ───────────────────────────────────
  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("token");
      if (!token) return navigate("/");
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const { email, contact_id, name, full_name, fullname } = payload || {};
        const prettyName =
          full_name || fullname || name || email || "My Profile";

        setDisplayName(`${prettyName} ▾`);

        const resp = await fetch(`${API_BASE}/contact/determine-access/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email, contact_id }),
        });
        const data = await resp.json();
        if (resp.ok) setUserAccess(data.access || []);
      } catch {
      } finally {
        setLoadingAccess(false);
      }
    })();
  }, [navigate]);

  const handleOwnerPortalClick = () => navigate("/owner-portal");
  const handleTrusteePortalClick = () => navigate("/trustee-portal");
  const handleContractorPortalClick = () => {
    navigate("/contractor-portal");
  };

  // small helper to refresh just the legal record
  const refetchLegal = async () => {
    const token = localStorage.getItem("token");
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_BASE}/legals/${id}/`, {
        headers,
      });
      if (res.ok) {
        const js = await res.json();
        setLegal(js);
        initEditFieldsFromLegal(js);
        try {
          const metaRes = await fetch(
            `${API_BASE}/legal/${id}/aod-file/meta/`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          if (metaRes.ok) {
            const meta = await metaRes.json();
            setAodMeta({
              exists: !!meta.exists,
              file_name: meta.file_name || null,
            });
          } else {
            setAodMeta({ exists: false, file_name: null });
          }
        } catch {}
      }
    } catch (e) {
      console.error("Failed to refresh legal after payment:", e);
    }
  };

  /* ────────── fetch everything ────────── */
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/");
      return;
    }

    const controller = new AbortController();

    const loadLegal = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE}/legals/${id}/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error("Could not load legal case.");
        }

        const legalData = await response.json();

        setLegal(legalData);
        setAttorneyRef(legalData.cr446_attorneyaccountreference || "");
        initEditFieldsFromLegal(legalData);

        document.title = `${legalData?.cj_name || "Legal"} • Legal Detail`;
      } catch (loadError) {
        if (loadError.name !== "AbortError") {
          setError(loadError.message || "Could not load legal case.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadLegal();

    return () => controller.abort();
  }, [id, navigate]);

  const saveAttorneyReference = async () => {
    if (!attorneyRef.trim()) return;

    setSavingAttorneyRef(true);

    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE}/legal/${id}/update/`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          attorney_reference: attorneyRef.trim(),
        }),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      const updated = await res.json();

      setLegal((current) => ({
        ...current,
        ...updated,
      }));
      setAttorneyRef(updated.cr446_attorneyaccountreference || attorneyRef);
    } catch (err) {
      alert("Unable to update Attorney Reference.");
    } finally {
      setSavingAttorneyRef(false);
    }
  };

  const fetchConductRulesDocs = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token || !id) return;

    setConductRulesLoading(true);
    setConductRulesError("");

    try {
      const res = await fetch(`${API_BASE}/legal/${id}/conduct-rules/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || "Failed to load conduct rules.");
      }

      setConductRulesDocs(Array.isArray(data?.value) ? data.value : []);
    } catch (e) {
      setConductRulesError(e.message || "Failed to load conduct rules.");
    } finally {
      setConductRulesLoading(false);
    }
  }, [API_BASE, id]);

  const [newNoteFile, setNewNoteFile] = useState(null);

  const handleAddTimelineNote = async () => {
    if (!newTimelineNote.trim() && !newNoteFile) return;

    setPostingNote(true);
    const token = localStorage.getItem("token");

    try {
      const fd = new FormData();
      fd.append("notetext", newTimelineNote.trim());

      if (newNoteFile) {
        fd.append("file", newNoteFile);
      }

      const res = await fetch(
        `${API_BASE}/legal/${id}/timeline-notes/create/`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        },
      );

      if (!res.ok) throw new Error("Add note failed");

      // Refresh timeline
      const nt = await fetch(`${API_BASE}/legals/${id}/timeline/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (nt.ok) {
        const js = await nt.json();
        const arr = Array.isArray(js) ? js : js.value || [];
        const sorted = [...arr].sort(
          (a, b) =>
            new Date(b.modifiedon || b.createdon) -
            new Date(a.modifiedon || a.createdon),
        );
        setNotes(sorted);
      }

      // Reset UI
      setNewTimelineNote("");
      setNewNoteFile(null);
      const fileEl = document.getElementById("timeline-note-file");
      if (fileEl) fileEl.value = "";
    } catch (err) {
      console.error(err);
      alert("Failed to add note.");
    } finally {
      setPostingNote(false);
    }
  };

  /* ────────── tabs / pages ────────── */
  const tabs = ["General", "Payments", "Invoices", "Timeline", "Ledger"];
  const [activeTab, setActiveTab] = useState("General");
  const perPage = 10;
  const [payPage, setPayPage] = useState(1);
  const [notePage, setNotePage] = useState(1);
  const [bcInvoices, setBcInvoices] = useState([]);
  const [bcInvoiceTotal, setBcInvoiceTotal] = useState(0);
  const [bcInvoicesLoading, setBcInvoicesLoading] = useState(false);
  const [bcInvoicesError, setBcInvoicesError] = useState("");
  const [bcInvoicesLoaded, setBcInvoicesLoaded] = useState(false);
  const [invoicePage, setInvoicePage] = useState(1);

  const defaultLedgerFrom = (() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 2, 1)
      .toISOString()
      .slice(0, 10);
  })();


  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState("");
  const [ledgerLoaded, setLedgerLoaded] = useState(false);
  const [ledgerPage, setLedgerPage] = useState(1);

  const [ledgerFilters, setLedgerFilters] = useState({
    account: "combined",
    from: defaultLedgerFrom,
    to: new Date().toISOString().slice(0, 10),
    document: "",
    external_document: "",
    description: "",
  });

  const fetchBcInvoices = useCallback(async () => {
    if (!id) return;

    setBcInvoicesLoading(true);
    setBcInvoicesError("");

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${API_BASE}/legal/${id}/bc-invoices/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.error || "Failed to retrieve Business Central invoices.",
        );
      }

      setBcInvoices(Array.isArray(data.value) ? data.value : []);

      setBcInvoiceTotal(Number(data.total_invoice_amount) || 0);

      setBcInvoicesLoaded(true);
    } catch (error) {
      setBcInvoicesError(
        error.message || "Failed to retrieve Business Central invoices.",
      );
    } finally {
      setBcInvoicesLoading(false);
    }
  }, [API_BASE, id]);

  useEffect(() => {
    if (activeTab === "Invoices" && !bcInvoicesLoaded && !bcInvoicesLoading) {
      fetchBcInvoices();
    }
  }, [activeTab, bcInvoicesLoaded, bcInvoicesLoading, fetchBcInvoices]);

  const buildLedgerQuery = useCallback(() => {
    const params = new URLSearchParams();

    Object.entries(ledgerFilters).forEach(([key, value]) => {
      if (String(value || "").trim()) {
        params.set(key, String(value).trim());
      }
    });

    return params.toString();
  }, [ledgerFilters]);

  const fetchLedger = useCallback(async () => {
    if (!id) return;

    setLedgerLoading(true);
    setLedgerError("");

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_BASE}/legal/${id}/ledger/?${buildLedgerQuery()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.error || "Failed to retrieve the customer ledger.",
        );
      }

      setLedger(data);
      setLedgerLoaded(true);
      setLedgerPage(1);
    } catch (error) {
      setLedgerError(
        error.message || "Failed to retrieve the customer ledger.",
      );
    } finally {
      setLedgerLoading(false);
    }
  }, [API_BASE, buildLedgerQuery, id]);

  const exportLedger = async () => {
    setLedgerError("");

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_BASE}/legal/${id}/ledger/export/?${buildLedgerQuery()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to export the customer ledger.");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");

      anchor.href = url;
      anchor.download = "legal_unit_ledger.xlsx";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setLedgerError(error.message || "Failed to export the customer ledger.");
    }
  };

  useEffect(() => {
    if (activeTab === "Ledger" && !ledgerLoaded && !ledgerLoading) {
      fetchLedger();
    }
  }, [activeTab, fetchLedger, ledgerLoaded, ledgerLoading]);
  // ────────── Load Payment submit ──────────
  const handleSubmitPayment = async () => {
    setPaymentMessage("");
    setPaymentError("");

    const { file, amount, invoice, invoiceDate } = paymentForm;

    if (!file) {
      setPaymentError(
        loadAsInvoice
          ? "Please choose an invoice file."
          : "Please choose a payment file.",
      );
      return;
    }

    if (amount === "" || isNaN(Number(amount))) {
      setPaymentError("Please enter a valid amount.");
      return;
    }

    if (!invoice || !String(invoice).trim()) {
      setPaymentError("Please enter the supplier invoice number.");
      return;
    }

    if (loadAsInvoice && !invoiceDate) {
      setPaymentError("Please enter the invoice date.");
      return;
    }

    setUploadingPayment(true);

    try {
      const formData = new FormData();

      formData.append("file", file);
      formData.append("amount", amount);
      formData.append("invoice", invoice);

      if (loadAsInvoice) {
        formData.append("invoiceDate", invoiceDate);
      }

      const token = localStorage.getItem("token");

      const userProfileId =
        localStorage.getItem("userProfileId") ||
        (() => {
          try {
            const decoded = JSON.parse(atob(token.split(".")[1] || ""));
            return decoded?.user_profile_id || "";
          } catch {
            return "";
          }
        })();

      if (userProfileId) {
        formData.append("userProfileId", userProfileId);
      }

      const response = await fetch(`${API_BASE}/legal/${id}/submit-payment/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      await refetchLegal();

      setPaymentMessage(
        loadAsInvoice
          ? "Invoice loaded successfully."
          : "Payment loaded successfully.",
      );
      setBcInvoicesLoaded(false);

      setPaymentForm({
        file: null,
        amount: "",
        invoice: "",
        invoiceDate: "",
      });

      const fileElement = document.getElementById("payment-file-input");

      if (fileElement) {
        fileElement.value = "";
      }
    } catch (error) {
      console.error(error);

      setPaymentError(
        loadAsInvoice ? "Failed to load invoice." : "Failed to load payment.",
      );
    } finally {
      setUploadingPayment(false);
    }
  };

  const handleStartEditing = () => {
    setSaveError("");
    initEditFieldsFromLegal(legal);
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setSaveError("");
    initEditFieldsFromLegal(legal);
    setIsEditing(false);
  };

  const handleSaveEditing = async () => {
    setSavingEdits(true);
    setSaveError("");
    try {
      const token = localStorage.getItem("token");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      const payload = Object.fromEntries(
        Object.entries(editFields).filter(([key, value]) => {
          const originalValue = originalEditFields[key];

          return String(value ?? "") !== String(originalValue ?? "");
        }),
      );

      // normalize number
      if (payload.aod_amount !== "" && !isNaN(payload.aod_amount)) {
        payload.aod_amount = Number(payload.aod_amount);
      }
      if (payload.aod_amount === "" || isNaN(payload.aod_amount)) {
        delete payload.aod_amount;
      }
      ["active_attorney_aod", "judgement", "nulla_bonna", "attachment"].forEach(
        (key) => {
          if (payload[key] === "Yes") payload[key] = true;
          else if (payload[key] === "No") payload[key] = false;
          else delete payload[key];
        },
      );

      // normalize Yes/No -> boolean; if unset, omit
      if (payload.active_attorney_aod === "Yes")
        payload.active_attorney_aod = true;
      else if (payload.active_attorney_aod === "No")
        payload.active_attorney_aod = false;
      else delete payload.active_attorney_aod;

      // 1) PATCH metadata (dates/amount/boolean/current_situation etc.)
      if (Object.keys(payload).length > 0) {
        const res = await fetch(`${API_BASE}/legal/${id}/update/`, {
          method: "PATCH",
          headers,
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const message = await res.text();
          throw new Error(message || "Failed to save changes");
        }

        const updated = await res.json();

        const mergedLegal = {
          ...legal,
          ...(updated || {}),
        };

        setLegal(mergedLegal);
        initEditFieldsFromLegal(mergedLegal);
      }

      // 2) Upload AOD file if user selected one (NEVER clear existing if none selected)
      // ✅ Upload AOD file as multipart/form-data (DO NOT set Content-Type yourself)
      if (aodFile) {
        const fdAod = new FormData();
        fdAod.append("file", aodFile, aodFile.name || "aod-file");

        const up = await fetch(`${API_BASE}/legal/${id}/upload/aod-file/`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`, // let the browser set the multipart boundary
          },
          body: fdAod,
        });

        if (!up.ok) {
          const t = await up.text();
          throw new Error(t || "AOD file upload failed");
        }
        await refetchLegal();
      }

      setIsEditing(false);
      setAodFile(null);
    } catch (e) {
      console.error(e);
      setSaveError(e?.message || "Failed to save changes.");
    } finally {
      setSavingEdits(false);
    }
  };

  /* ────────────────────────────────────────────────────────────────
     NEW: Download Statement (modal + API calls)
     Mirrors DownloadStatementPage usage and body shape.
  ───────────────────────────────────────────────────────────────── */
  const [showStmtModal, setShowStmtModal] = useState(false);
  const [stmtFrom, setStmtFrom] = useState("");
  const [stmtTo, setStmtTo] = useState("");
  const [stmtError, setStmtError] = useState("");
  const [stmtSuccess, setStmtSuccess] = useState("");
  const [stmtLoadingType, setStmtLoadingType] = useState(null); // 'pdf' | 'excel' | null
  const [stmtAccountChoice, setStmtAccountChoice] = useState("main");

  // NEW: Email destination controls
  const [stmtEmailMode, setStmtEmailMode] = useState("profile"); // 'profile' | 'custom'
  const [stmtCustomEmail, setStmtCustomEmail] = useState("");

  const openStatementModal = () => {
    const today = new Date().toISOString().slice(0, 10);

    setStmtError("");
    setStmtSuccess("");
    setStmtTo(today);

    // Prefer sub account when one exists; otherwise use main account.
    setStmtAccountChoice(legal?._cr446_subaccount_value ? "sub" : "main");

    setShowStmtModal(true);
  };
  const closeStatementModal = () => {
    setShowStmtModal(false);
  };

  const handleStatement = async (delivery) => {
    setStmtError("");
    setStmtSuccess("");

    if (!stmtTo) {
      setStmtError("Please select a To Date.");
      return;
    }

    if (stmtFrom && new Date(stmtFrom) > new Date(stmtTo)) {
      setStmtError("Invalid date range.");
      return;
    }

    const token = localStorage.getItem("token");
    const statementFromDate = stmtFrom || "1900-01-01";

    const mainAccountId = legal?._cj_account_value;
    const subAccountId = legal?._cr446_subaccount_value;

    const gmaAccountNumberID =
      stmtAccountChoice === "sub" && subAccountId
        ? subAccountId
        : mainAccountId;
    const selectedAccountName =
      stmtAccountChoice === "sub"
        ? legal?.[
            "_cr446_subaccount_value@OData.Community.Display.V1.FormattedValue"
          ]
        : legal?.[
            "_cj_account_value@OData.Community.Display.V1.FormattedValue"
          ];

    const safeSelectedAccountName = (selectedAccountName || "account").replace(
      /[^a-zA-Z0-9-_]/g,
      "_",
    );

    const profileEmail =
      localStorage.getItem("userEmail") ||
      (() => {
        try {
          return JSON.parse(atob(token.split(".")[1] || ""))?.email || "";
        } catch {
          return "";
        }
      })();

    const destinationEmail =
      stmtEmailMode === "custom" ? stmtCustomEmail.trim() : profileEmail;

    if (delivery === "email" && !destinationEmail) {
      setStmtError("Please select or enter an email address.");
      return;
    }

    setStmtLoadingType(`${delivery}-${stmtFormat}`);

    try {
      const res = await fetch(`${API_BASE}/legal/${id}/statement/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          delivery,
          format: stmtFormat,
          accountId: gmaAccountNumberID,
          from: statementFromDate,
          to: stmtTo,
          email: delivery === "email" ? destinationEmail : "",
        }),
      });

      if (!res.ok) throw new Error(await res.text());

      if (delivery === "download") {
        const blob = await res.blob();
        const ext = stmtFormat === "excel" ? "xlsx" : "pdf";
        const url = window.URL.createObjectURL(blob);

        const contentDisposition = res.headers.get("Content-Disposition") || "";
        const match = contentDisposition.match(/filename="?([^"]+)"?/i);

        const backendFilename =
          match?.[1] ||
          `Statement_${safeSelectedAccountName}_${new Date()
            .toISOString()
            .slice(0, 10)}.${ext}`;

        const a = document.createElement("a");
        a.href = url;
        a.download = backendFilename;
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);
        setStmtSuccess("Statement downloaded.");
      } else {
        setStmtSuccess(`Statement emailed to ${destinationEmail}.`);
      }
    } catch (e) {
      setStmtError(e.message || "Statement failed.");
    } finally {
      setStmtLoadingType(null);
    }
  };

  /* ────────── early exits ────────── */
  if (loading)
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <main className={styles.contentArea}>
            <div className={styles.panel}>
              <div className={styles.loadingContainer}>
                <div className={styles.loadingImage}></div>
                <p className={styles.loadingText}>Loading...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  if (error) return <p className={styles.errorText}>{error}</p>;
  if (!legal) return null;

  // Infer profile email for display in the modal (non-blocking; UI only)
  let inferredProfileEmail = "";
  try {
    inferredProfileEmail = localStorage.getItem("userEmail") || "";
    if (!inferredProfileEmail) {
      const token = localStorage.getItem("token");
      if (token) {
        const decoded = JSON.parse(atob(token.split(".")[1] || ""));
        if (decoded?.email) inferredProfileEmail = decoded.email;
      }
    }
  } catch {
    // ignore
  }

  /* ────────── ui ────────── */
  return (
    <div className={styles.page}>
      {/* ═══ NAVBAR ═══ */}
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <img src={logoimage} alt="GMA Logo" className={styles.topLogo} />

          {/* HAMBURGER: opens/closes sidebar */}
          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            ☰
          </button>

          <h1 className={styles.topTitle}>{legal?.cj_name || "Legal Case"}</h1>
        </div>

        {/* USER CHIP: opens dropdown */}
        <div
          className={styles.userChip}
          onClick={() => {
            navigate("/user-profile");
          }}
          style={{ cursor: "pointer" }}
        >
          <span className={styles.statusDot}></span>
          <span className={styles.userName}>{displayName}</span>
        </div>
      </header>
      <div className={styles.shell}>
        <PortalSidebar
          activePortal="contractor"
          activeTab="legal-files"
          userAccess={userAccess}
          loadingAccess={loadingAccess}
          sidebarOpen={sidebarOpen}
          ticketRole={null}
        />
        <main className={styles.contentArea}>
          <div className={styles.panel}>
            {/* ═══ SUMMARY ═══ */}
            <div className={styles.paymentHeaderCard}>
              {/* BACK BUTTON – top left */}
              <button
                className={styles.paymentBackBtn}
                onClick={() => navigate(-1)}
              >
                Back
              </button>

              {/* TITLE */}
              <h1 className={styles.paymentHeaderTitle}>
                {legal?.cj_name || "Legal Case"}
              </h1>

              {/* DOWNLOAD BUTTON – top right */}
              <div className={styles.topRightBtnWrapper}>
                <button
                  className={styles.downloadBtn}
                  onClick={openStatementModal}
                >
                  Download Statement
                </button>
              </div>

              {/* DIVIDER */}
              <div className={styles.paymentHeaderDivider} />

              {/* HEADER LABELS */}
              <div className={styles.paymentHeaderGrid3}>
                <div className={styles.paymentHeaderLabelItem}>Case Name:</div>
                <div className={styles.paymentHeaderLabelItem}>Progress:</div>
                <div className={styles.paymentHeaderLabelItem}>
                  Next Follow Up:
                </div>
              </div>

              {/* VALUE PILL */}
              <div className={styles.paymentHeaderValuePill}>
                <div className={styles.paymentHeaderGrid3}>
                  <div className={styles.paymentHeaderValueItem}>
                    {display(legal, "cj_name")}
                  </div>

                  <div className={styles.paymentHeaderValueItem}>
                    {display(legal, "cj_progress")}
                  </div>

                  <div className={styles.paymentHeaderValueItem}>
                    {isDateish(legal?.cj_nextfollowupdate)
                      ? fmtDate(legal?.cj_nextfollowupdate)
                      : display(legal, "cj_nextfollowupdate")}
                  </div>
                </div>
              </div>
            </div>
            {/* ═══ TABS ═══ */}
            <div className={styles.subTabs}>
              {tabs.map((t) => (
                <div
                  key={t}
                  className={
                    activeTab === t ? styles.subTabActive : styles.subTab
                  }
                  onClick={() => {
                    setActiveTab(t);
                    setPayPage(1);
                    setInvoicePage(1);
                    setNotePage(1);
                    setLedgerPage(1);
                  }}
                >
                  {t}
                </div>
              ))}
            </div>

            {/* ═══ TAB CONTENT ═══ */}
            <div className={styles.paymentCardGrid}>
              {/* ── GENERAL ── */}
              {activeTab === "General" && (
                <>
                  <div className={styles.paymentCardDark}>
                    <h2 className={styles.cardSectionTitle}>General</h2>

                    {/* Attorney Reference */}
                    <div className={styles.inputGroup}>
                      <h3 className={styles.inputLabel}>Attorney Ref.:</h3>

                      <input
                        className={styles.inputField}
                        value={attorneyRef}
                        placeholder="Required — maximum 8 characters"
                        maxLength={8}
                        onChange={(e) => setAttorneyRef(e.target.value)}
                        disabled={savingAttorneyRef}
                        style={{
                          borderColor: attorneyRef.trim()
                            ? undefined
                            : "#dc3545",
                          color: attorneyRef.trim() ? "#fff" : "#dc3545",
                          caretColor: "#fff",
                        }}
                      />

                      <button
                        type="button"
                        className={styles.downloadBtn}
                        onClick={saveAttorneyReference}
                        disabled={savingAttorneyRef || !attorneyRef.trim()}
                        style={{ marginTop: 10 }}
                      >
                        {savingAttorneyRef ? "Saving..." : "Save"}
                      </button>
                    </div>

                    {[
                      ["Attorney", "_cj_attorney_value"],
                      ["Current Account", "_cj_account_value"],
                      ["Legal Account", "_cr446_subaccount_value"],
                      ["Handover Date", "cj_handoverdate"],
                      ["Debt on Handover", "cj_debtonhandover"],
                    ].map(([lbl, k]) => (
                      <div key={k} className={styles.inputGroup}>
                        <h3 className={styles.inputLabel}>{lbl}:</h3>
                        <input
                          readOnly
                          className={styles.inputField}
                          value={
                            k === "cj_handoverdate"
                              ? fmtDate(legal?.[k])
                              : display(legal, k)
                          }
                        />
                      </div>
                    ))}

                    <div className={styles.inputGroup}>
                      <h3 className={styles.inputLabel}>Conduct Rules:</h3>

                      {conductRulesLoading ? (
                        <span className={styles.infoText}>Loading...</span>
                      ) : conductRulesError ? (
                        <span className={styles.errorText}>Unavailable</span>
                      ) : conductRulesDocs.length > 0 ? (
                        <button
                          className={styles.downloadBtn}
                          onClick={() => {
                            window.open(
                              `${API_BASE.replace("/api", "")}${conductRulesDocs[0].downloadUrl}`,
                              "_blank",
                            );
                          }}
                        >
                          Download Conduct Rules
                        </button>
                      ) : (
                        <span className={styles.infoText}>
                          No Conduct Rules Available
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Case events (editable when editing) */}
                  <div className={styles.paymentCard}>
                    <h2 className={styles.cardSectionTitleLight}>
                      Case Events
                    </h2>

                    {[
                      ["Letter of Demand", "cr446_letterofdemand"],
                      ["Summons Sent To Court", "cr446_summonssenttocourt"],
                      ["Summons Served", "cj_summonsserved"],
                      ["Judgement", "cr446_judgement"],
                      ["Judgement Granted", "cj_judgementgranted"],
                      ["Warrant of Execution", "cr446_warrantofexecution"],
                      ["Nulla Bonna", "cr446_nullabonna"],
                      ["Attachment", "cr446_attachment"],
                      ["Rule 46 Application", "cr446_rule46application"],
                      ["Section 65", "cj_section65"],
                      ["Section 66", "cj_section66"],
                    ].map(([lbl, k], idx) => {
                      const eventDateKeys = new Set([
                        "cr446_letterofdemand",
                        "cr446_summonssenttocourt",
                        "cj_summonsserved",
                        "cr446_warrantofexecution",
                        "cj_judgementgranted",
                        "cr446_rule46application",
                        "cj_section65",
                        "cj_section66",
                      ]);
                      const fieldKeyMap = {
                        cr446_letterofdemand: "letter_of_demand",
                        cr446_summonssenttocourt: "summons_sent_to_court",
                        cj_summonsserved: "summons_served",
                        cr446_warrantofexecution: "warrant_of_execution",
                        cr446_judgement: "judgement",
                        cj_judgementgranted: "judgement_granted",
                        cr446_nullabonna: "nulla_bonna",
                        cr446_attachment: "attachment",
                        cr446_rule46application: "rule46_application",
                        cj_section65: "section65",
                        cj_section66: "section66",
                      };
                      const friendly = fieldKeyMap[k];
                      const isDate = eventDateKeys.has(k);

                      return (
                        <div
                          key={`${k}-${idx}`}
                          className={styles.inputGroupLight}
                        >
                          <h3 className={styles.inputLabelLight}>{lbl}:</h3>

                          {/* Read-only or Editable */}
                          {!isEditing || !friendly ? (
                            <input
                              readOnly
                              className={styles.inputFieldLight}
                              value={
                                isDate ? fmtDate(legal?.[k]) : display(legal, k)
                              }
                            />
                          ) : [
                              "cr446_judgement",
                              "cr446_nullabonna",
                              "cr446_attachment",
                            ].includes(k) ? (
                            <select
                              className={styles.inputFieldLight}
                              value={editFields[friendly] || ""}
                              onChange={(e) =>
                                setEditFields((s) => ({
                                  ...s,
                                  [friendly]: e.target.value,
                                }))
                              }
                            >
                              <option value="">—</option>
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                            </select>
                          ) : isDate ? (
                            <input
                              type="date"
                              className={styles.inputFieldLight}
                              value={editFields[friendly] || ""}
                              onChange={(e) =>
                                setEditFields((s) => ({
                                  ...s,
                                  [friendly]: e.target.value,
                                }))
                              }
                            />
                          ) : (
                            <input
                              readOnly
                              className={styles.inputFieldLight}
                              value={display(legal, k)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Attorney AOD (editable when editing) */}
                  {(() => {
                    const hasAOD =
                      legal?.cr446_activeattorneyaod !== undefined ||
                      legal?.["cr446_attorneyaodfile@odata.mediaReadLink"] ||
                      legal?.cj_attorneyaodstartdate ||
                      legal?.cj_aodenddate ||
                      legal?.cj_aodamount;
                    return isEditing || hasAOD;
                  })() && (
                    <div className={styles.paymentCard}>
                      <h2 className={styles.cardSectionTitleLight}>
                        Attorney AOD Information
                      </h2>

                      {/* Active Attorney AOD (Yes/No) */}
                      {/* Main Account Age Total */}
                      <div className={styles["input-group"]}>
                        <h3 style={{ color: "#9b1c31" }}>
                          {legal?.[
                            "_cj_account_value@OData.Community.Display.V1.FormattedValue"
                          ] || "Current Account"}{" "}
                          Age Total:
                        </h3>

                        <input
                          readOnly
                          className={`${styles.inputField} ${getAgeTotalClass(
                            legal?.main_account_age_total,
                          )}`}
                          value={formatAgeTotal(legal?.main_account_age_total)}
                        />
                      </div>

                      {/* Sub Account Age Total */}
                      {legal?._cr446_subaccount_value && (
                        <div className={styles["input-group"]}>
                          <h3 style={{ color: "#9b1c31" }}>
                            {legal?.[
                              "_cr446_subaccount_value@OData.Community.Display.V1.FormattedValue"
                            ] || "Legal Account"}{" "}
                            Age Total:
                          </h3>

                          <input
                            readOnly
                            className={`${styles.inputField} ${getAgeTotalClass(
                              legal?.sub_account_age_total,
                            )}`}
                            value={formatAgeTotal(legal?.sub_account_age_total)}
                          />
                        </div>
                      )}

                      {/* Combined Age Total */}
                      <div className={styles["input-group"]}>
                        <h3 style={{ color: "#9b1c31" }}>
                          Combined Age Total:
                        </h3>

                        <input
                          readOnly
                          className={`${styles.inputField} ${getAgeTotalClass(
                            legal?.combined_account_age_total,
                          )}`}
                          value={formatAgeTotal(
                            legal?.combined_account_age_total,
                          )}
                        />
                      </div>
                      <div className={styles["input-group"]}>
                        <h3>Active Attorney AOD:</h3>
                        {!isEditing ? (
                          <input
                            readOnly
                            value={display(legal, "cr446_activeattorneyaod")}
                            className={styles.inputField}
                          />
                        ) : (
                          <select
                            className={styles.editableSelect}
                            value={editFields.active_attorney_aod}
                            onChange={(e) =>
                              setEditFields((s) => ({
                                ...s,
                                active_attorney_aod: e.target.value,
                              }))
                            }
                          >
                            <option value="">—</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </select>
                        )}
                      </div>

                      {/* Attorney AOD File (download-only when not editing) */}
                      <div className={styles["input-group"]}>
                        <h3>Attorney AOD File:</h3>

                        {!isEditing ? (
                          legal?.[
                            "cr446_attorneyaodfile@odata.mediaReadLink"
                          ] || aodMeta.exists ? (
                            <button
                              aria-label="Download Attorney AOD file"
                              onClick={() => {
                                const url = `${API_BASE}/legal/${id}/download/aod-file/`;
                                const a = document.createElement("a");
                                a.href = url;
                                if (aodMeta?.file_name)
                                  a.download = aodMeta.file_name;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                              }}
                            >
                              Download
                            </button>
                          ) : (
                            <span className={styles.muted}>
                              No file uploaded
                            </span>
                          )
                        ) : (
                          <input
                            type="file"
                            className={styles.editableInput}
                            onChange={(e) =>
                              setAodFile(e.target.files?.[0] || null)
                            }
                          />
                        )}
                      </div>

                      {/* AOD Start Date */}
                      <div className={styles["input-group"]}>
                        <h3>AOD Start Date:</h3>
                        {!isEditing ? (
                          <input
                            readOnly
                            value={fmtDate(legal?.cj_attorneyaodstartdate)}
                            className={styles.inputField}
                          />
                        ) : (
                          <input
                            type="date"
                            className={styles.editableInput}
                            value={editFields.aod_start_date || ""}
                            onChange={(e) =>
                              setEditFields((s) => ({
                                ...s,
                                aod_start_date: e.target.value,
                              }))
                            }
                          />
                        )}
                      </div>

                      {/* AOD End Date */}
                      <div className={styles["input-group"]}>
                        <h3>AOD End Date:</h3>
                        {!isEditing ? (
                          <input
                            readOnly
                            value={fmtDate(legal?.cj_aodenddate)}
                            className={styles.inputField}
                          />
                        ) : (
                          <input
                            type="date"
                            className={styles.editableInput}
                            value={editFields.aod_end_date || ""}
                            onChange={(e) =>
                              setEditFields((s) => ({
                                ...s,
                                aod_end_date: e.target.value,
                              }))
                            }
                          />
                        )}
                      </div>

                      {/* AOD Amount */}
                      <div className={styles["input-group"]}>
                        <h3>AOD Amount:</h3>
                        {!isEditing ? (
                          <input
                            readOnly
                            value={display(legal, "cj_aodamount") ?? ""}
                            className={styles.inputField}
                          />
                        ) : (
                          <input
                            type="number"
                            step="0.01"
                            className={styles.editableInput}
                            value={editFields.aod_amount}
                            onChange={(e) =>
                              setEditFields((s) => ({
                                ...s,
                                aod_amount: e.target.value,
                              }))
                            }
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {/* NEW: Load Payment card */}
                  <div className={styles.paymentCard}>
                    <h2>{loadAsInvoice ? "Load Invoice" : "Load Payment"}</h2>
                    <div className={styles["input-group"]}>
                      <h3>
                        {loadAsInvoice ? "Invoice File:" : "Payment File:"}
                      </h3>
                      <input
                        id="payment-file-input"
                        type="file"
                        className={styles.editableInput}
                        onChange={(e) =>
                          setPaymentForm((s) => ({
                            ...s,
                            file: e.target.files?.[0] || null,
                          }))
                        }
                      />
                    </div>
                    <div className={styles["input-group"]}>
                      <h3>Amount (R):</h3>
                      <input
                        type="number"
                        step="0.01"
                        className={styles.inputField}
                        value={paymentForm.amount}
                        onChange={(e) =>
                          setPaymentForm((s) => ({
                            ...s,
                            amount: e.target.value,
                          }))
                        }
                        placeholder="0.00"
                      />
                    </div>
                    <div className={styles["input-group"]}>
                      <h3>Invoice No.:</h3>
                      <input
                        type="text"
                        className={styles.inputField}
                        value={paymentForm.invoice}
                        onChange={(e) =>
                          setPaymentForm((s) => ({
                            ...s,
                            invoice: e.target.value,
                          }))
                        }
                        placeholder="e.g. SUP-12345"
                      />
                    </div>

                    {loadAsInvoice && (
                      <div className={styles["input-group"]}>
                        <h3>Inv Date:</h3>

                        <input
                          type="date"
                          className={styles.inputField}
                          value={paymentForm.invoiceDate}
                          onChange={(e) =>
                            setPaymentForm((current) => ({
                              ...current,
                              invoiceDate: e.target.value,
                            }))
                          }
                        />
                      </div>
                    )}

                    {paymentError ? (
                      <p className={styles.errorText} style={{ marginTop: 8 }}>
                        {paymentError}
                      </p>
                    ) : null}
                    {paymentMessage ? (
                      <p className={styles.infoText} style={{ marginTop: 8 }}>
                        {paymentMessage}
                      </p>
                    ) : null}

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 10,
                        marginTop: 8,
                      }}
                    >
                      <button
                        type="button"
                        onClick={handleSubmitPayment}
                        disabled={uploadingPayment}
                      >
                        {uploadingPayment
                          ? "Loading…"
                          : loadAsInvoice
                            ? "Load Invoice"
                            : "Load Payment"}
                      </button>
                    </div>
                  </div>

                  {/* Current situation (contains Edit/Save/Cancel) */}
                  <div className={styles.paymentCard}>
                    <div className={styles.csHeader}>
                      <h2>Current Situation</h2>
                      {!isEditing ? (
                        <button onClick={handleStartEditing}>Edit</button>
                      ) : (
                        <div className={styles.inlineActions}>
                          <button
                            onClick={handleSaveEditing}
                            disabled={savingEdits}
                          >
                            {savingEdits ? "Saving…" : "Save"}
                          </button>
                          <button
                            onClick={handleCancelEditing}
                            disabled={savingEdits}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>

                    {saveError ? (
                      <p className={styles.errorText}>{saveError}</p>
                    ) : null}

                    {!isEditing ? (
                      <p className={styles.infoText}>
                        {legal?.cj_currentsituationdescription ?? "—"}
                      </p>
                    ) : (
                      <textarea
                        className={styles.editableTextarea}
                        value={editFields.current_situation}
                        onChange={(e) =>
                          setEditFields((s) => ({
                            ...s,
                            current_situation: e.target.value,
                          }))
                        }
                        placeholder="Describe the current situation…"
                      />
                    )}
                  </div>
                </>
              )}

              {/* ── PAYMENTS ── */}
              {activeTab === "Payments" && (
                <div className={styles.paymentCard}>
                  <h2>Payments</h2>
                  <div className={styles.responsiveTableWrapper}>
                    <table className={styles.dataTable}>
                      <thead>
                        <tr>
                          <th>Payment Number</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments
                          .slice((payPage - 1) * perPage, payPage * perPage)
                          .map((p) => (
                            <tr key={p.cr446_paymentid}>
                              <td className={styles.paymentNumberCell}>
                                {p.cr446_paymentpk}
                              </td>
                              <td className={styles.amountCell}>
                                <span className={styles.currency}>R</span>
                                <span className={styles.amountValue}>
                                  {formatAmount(p.cr446_amount)}
                                </span>
                              </td>
                              <td>
                                {p[
                                  "cj_progress@OData.Community.Display.V1.FormattedValue"
                                ] || p.cj_progress}
                              </td>
                              <td>
                                {fmtDate(p.cj_paymenttime || p.createdon)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    page={payPage}
                    total={payments.length}
                    perPage={perPage}
                    onChange={setPayPage}
                  />
                </div>
              )}

              {/* ── BUSINESS CENTRAL INVOICES ── */}
              {activeTab === "Invoices" && (
                <div className={styles.paymentCard}>
                  <h2>Invoices</h2>

                  <div
                    className={styles.infoText}
                    style={{
                      marginBottom: 16,
                      fontSize: "1.05rem",
                    }}
                  >
                    <strong>Total Invoice Amount:</strong> R{" "}
                    {formatAmount(bcInvoiceTotal)}
                  </div>

                  {bcInvoicesLoading ? (
                    <div className={styles.loadingContainer}>
                      <div className={styles.loadingImage}></div>
                      <p className={styles.loadingText}>
                        Loading Business Central invoices...
                      </p>
                    </div>
                  ) : bcInvoicesError ? (
                    <div>
                      <p className={styles.errorText}>{bcInvoicesError}</p>

                      <button
                        type="button"
                        onClick={() => {
                          setBcInvoicesLoaded(false);
                          fetchBcInvoices();
                        }}
                      >
                        Retry
                      </button>
                    </div>
                  ) : bcInvoices.length === 0 ? (
                    <p className={styles.infoText}>
                      No Business Central invoices or payments found.
                    </p>
                  ) : (
                    <>
                      <div className={styles.responsiveTableWrapper}>
                        <table className={styles.dataTable}>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Document Number</th>
                              <th>Description</th>
                              <th>Amount</th>
                              <th>Invoice Number</th>
                            </tr>
                          </thead>

                          <tbody>
                            {bcInvoices
                              .slice(
                                (invoicePage - 1) * perPage,
                                invoicePage * perPage,
                              )
                              .map((entry, index) => (
                                <tr
                                  key={
                                    entry.entry_number ||
                                    `${entry.document_number}-${index}`
                                  }
                                >
                                  <td>{fmtDate(entry.posting_date) || "—"}</td>
                                  <td>{entry.document_number || "—"}</td>
                                  <td>{entry.description || "—"}</td>

                                  <td className={styles.amountCell}>
                                    <span className={styles.currency}></span>

                                    <span className={styles.amountValue}>
                                      {formatAmount(entry.amount)}
                                    </span>
                                  </td>

                                  <td>{entry.invoice_number || "—"}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>

                      <Pagination
                        page={invoicePage}
                        total={bcInvoices.length}
                        perPage={perPage}
                        onChange={setInvoicePage}
                      />
                    </>
                  )}
                </div>
              )}

              {/* ── TIMELINE NOTES ────────── */}
              {activeTab === "Timeline" && (
                <div className={styles.paymentCard}>
                  <h2>Current Situation</h2>
                  <p className={styles.infoText}>
                    {legal?.cj_currentsituationdescription ?? "—"}
                  </p>

                  {/* ── ADD NEW TIMELINE NOTE ── */}
                  <div className={styles.commentSection}>
                    <textarea
                      className={styles.commentInput}
                      placeholder="Add a timeline note..."
                      value={newTimelineNote}
                      onChange={(e) => setNewTimelineNote(e.target.value)}
                    />

                    <input
                      id="timeline-note-file"
                      type="file"
                      className={styles.editableInput}
                      onChange={(e) =>
                        setNewNoteFile(e.target.files?.[0] || null)
                      }
                      style={{ marginTop: "10px" }}
                    />

                    <button
                      className={styles.commentButton}
                      onClick={handleAddTimelineNote}
                      disabled={
                        postingNote || (!newTimelineNote.trim() && !newNoteFile)
                      }
                    >
                      {postingNote ? "Adding…" : "Add Note"}
                    </button>
                  </div>

                  <h2>Timeline</h2>
                  {notes.length === 0 ? (
                    <p className={styles.infoText}>No notes available.</p>
                  ) : (
                    <>
                      <div className={styles.responsiveTableWrapper}>
                        <table className={styles.dataTable}>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Note</th>
                              <th>Created By</th>
                            </tr>
                          </thead>
                          <tbody>
                            {notes
                              .slice(
                                (notePage - 1) * perPage,
                                notePage * perPage,
                              )
                              .map((n) => (
                                <tr key={n.annotationid}>
                                  <td>
                                    {fmtDate(n.modifiedon || n.createdon)}
                                  </td>
                                  <td>
                                    <div
                                      className={styles.htmlContent}
                                      dangerouslySetInnerHTML={{
                                        __html: transformNoteHtml(n.notetext),
                                      }}
                                    />
                                  </td>
                                  <td>
                                    {
                                      n[
                                        "_createdby_value@OData.Community.Display.V1.FormattedValue"
                                      ]
                                    }
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                      <Pagination
                        page={notePage}
                        total={notes.length}
                        perPage={perPage}
                        onChange={setNotePage}
                      />
                    </>
                  )}
                </div>
              )}

              {activeTab === "Ledger" && (
                <div className={`${styles.paymentCard} ${styles.ledgerCard}`}>
                  <div className={styles.ledgerHeader}>
                    <div>
                      <h2>Customer Ledger</h2>
                      <p className={styles.infoText}>
                        Current account and active legal accounts for this unit.
                      </p>
                    </div>

                    <button
                      type="button"
                      className={styles.downloadBtn}
                      onClick={exportLedger}
                      disabled={ledgerLoading}
                    >
                      Export Filtered Excel
                    </button>
                  </div>

                  <div className={styles.ledgerFilters}>
                    <label>
                      Account
                      <select
                        className={styles.editableSelect}
                        value={ledgerFilters.account}
                        onChange={(event) =>
                          setLedgerFilters((current) => ({
                            ...current,
                            account: event.target.value,
                          }))
                        }
                      >
                        <option value="combined">Combined Unit Ledger</option>
                        <option value="current">Current Account</option>

                        {(ledger?.accounts || [])
                          .filter((account) => account.account_type === "legal")
                          .map((account) => (
                            <option
                              key={account.account_id}
                              value={account.account_id}
                            >
                              Legal Account — {account.account_number}
                            </option>
                          ))}
                      </select>
                    </label>

                    <label>
                      From Date
                      <input
                        type="date"
                        className={styles.editableInput}
                        value={ledgerFilters.from}
                        onChange={(event) =>
                          setLedgerFilters((current) => ({
                            ...current,
                            from: event.target.value,
                          }))
                        }
                      />
                    </label>

                    <label>
                      To Date
                      <input
                        type="date"
                        className={styles.editableInput}
                        value={ledgerFilters.to}
                        onChange={(event) =>
                          setLedgerFilters((current) => ({
                            ...current,
                            to: event.target.value,
                          }))
                        }
                      />
                    </label>

                    <label>
                      Document Number
                      <input
                        className={styles.editableInput}
                        value={ledgerFilters.document}
                        onChange={(event) =>
                          setLedgerFilters((current) => ({
                            ...current,
                            document: event.target.value,
                          }))
                        }
                      />
                    </label>

                    <label>
                      External Document Number
                      <input
                        className={styles.editableInput}
                        value={ledgerFilters.external_document}
                        onChange={(event) =>
                          setLedgerFilters((current) => ({
                            ...current,
                            external_document: event.target.value,
                          }))
                        }
                      />
                    </label>

                    <label>
                      Description
                      <input
                        className={styles.editableInput}
                        value={ledgerFilters.description}
                        onChange={(event) =>
                          setLedgerFilters((current) => ({
                            ...current,
                            description: event.target.value,
                          }))
                        }
                      />
                    </label>

                    <button
                      type="button"
                      className={styles.commentButton}
                      onClick={fetchLedger}
                      disabled={ledgerLoading}
                    >
                      {ledgerLoading ? "Loading…" : "Apply Filters"}
                    </button>
                  </div>

                  {ledgerError && (
                    <p className={styles.errorText}>{ledgerError}</p>
                  )}

                  {ledger && (
                    <>
                      <div className={styles.ledgerSummaryGrid}>
                        <div>
                          <span>Opening Balance {ledgerFilters.from}</span>
                          <strong>
                            R {formatAmount(ledger.opening_balance)}
                          </strong>
                        </div>

                        <div>
                          <span>Closing Balance According To This View</span>
                          <strong>
                            R {formatAmount(ledger.filtered_balance)}
                          </strong>
                        </div>

                        {(ledger.accounts || []).map((account) => (
                          <div key={account.account_id}>
                            <span>
                              {account.account_number} — Monthly Average Calculated Over The Last 3 Months (Debits Only)
                            </span>
                            <strong>
                              R{" "}
                              {formatAmount(account.three_month_average_debits)}
                            </strong>
                          </div>
                        ))}
                      </div>

                      <div className={styles.responsiveTableWrapper}>
                        <table className={styles.dataTable}>
                          <thead>
                            <tr>
                              <th>Account</th>
                              <th>Date</th>
                              <th>Document Number</th>
                              <th>Description</th>
                              <th>External Document Number</th>
                              <th>Credit Amount</th>
                              <th>Debit Amount</th>
                              <th>Amount</th>
                              <th>Balance to Date</th>
                            </tr>
                          </thead>

                          <tbody>
                            {(ledger.entries || [])
                              .slice(
                                (ledgerPage - 1) * perPage,
                                ledgerPage * perPage,
                              )
                              .map((entry) => (
                                <tr
                                  key={`${entry.account_id}-${entry.entry_number}`}
                                >
                                  <td>
                                    <strong>{entry.account_number}</strong>
                                    <br />
                                    <small>
                                      {entry.account_type === "current"
                                        ? "Current Account"
                                        : "Legal Account"}
                                    </small>
                                  </td>
                                  <td>{fmtDate(entry.date)}</td>
                                  <td>{entry.document_number || "—"}</td>
                                  <td>{entry.description || "—"}</td>
                                  <td>
                                    {entry.external_document_number || "—"}
                                  </td>
                                  <td>R {formatAmount(entry.credit_amount)}</td>
                                  <td>R {formatAmount(entry.debit_amount)}</td>
                                  <td>R {formatAmount(entry.amount)}</td>
                                  <td>
                                    R {formatAmount(entry.balance_to_date)}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>

                      <Pagination
                        page={ledgerPage}
                        total={(ledger.entries || []).length}
                        perPage={perPage}
                        onChange={setLedgerPage}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {showStmtModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Download Statement"
          className={styles.modalOverlay}
          onClick={(e) => e.target === e.currentTarget && closeStatementModal()}
        >
          <div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.key === "Escape" && closeStatementModal()}
            tabIndex={-1}
          >
            {/* HEADER */}
            <div className={styles.modalHeaderRow}>
              <h2 className={styles.modalTitle}>DOWNLOAD STATEMENT</h2>
              <button
                className={styles.modalClose}
                onClick={closeStatementModal}
              >
                ✕
              </button>
            </div>

            {/* BODY */}
            <div className={styles.modalBody}>
              <p className={styles.infoText}>
                Select a date range for the account linked to this record.
              </p>

              {/* SUCCESS */}
              {!!stmtSuccess && (
                <div
                  className={styles.infoText}
                  style={{
                    background: "rgba(76,175,80,0.12)",
                    border: "1px solid rgba(76,175,80,0.4)",
                    padding: "10px 12px",
                    borderRadius: "10px",
                  }}
                >
                  {stmtSuccess}
                </div>
              )}

              {/* ERROR */}
              {!!stmtError && (
                <div
                  className={styles.errorText}
                  style={{
                    background: "rgba(244,67,54,0.12)",
                    border: "1px solid rgba(244,67,54,0.4)",
                    padding: "10px 12px",
                    borderRadius: "10px",
                  }}
                >
                  {stmtError}
                </div>
              )}

              {/* DATE FIELDS */}
              <div className={styles.inputGroup}>
                {legal?._cr446_subaccount_value && (
                  <div className={styles.inputGroup}>
                    <h3>Account For Statement:</h3>

                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="radio"
                        name="stmt-account-choice"
                        value="main"
                        checked={stmtAccountChoice === "main"}
                        onChange={() => setStmtAccountChoice("main")}
                      />
                      <span>
                        Current Account
                        {legal?.[
                          "_cj_account_value@OData.Community.Display.V1.FormattedValue"
                        ]
                          ? ` (${legal["_cj_account_value@OData.Community.Display.V1.FormattedValue"]})`
                          : ""}
                      </span>
                    </label>

                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="radio"
                        name="stmt-account-choice"
                        value="sub"
                        checked={stmtAccountChoice === "sub"}
                        onChange={() => setStmtAccountChoice("sub")}
                      />
                      <span>
                        Legal Account
                        {legal?.[
                          "_cr446_subaccount_value@OData.Community.Display.V1.FormattedValue"
                        ]
                          ? ` (${legal["_cr446_subaccount_value@OData.Community.Display.V1.FormattedValue"]})`
                          : ""}
                      </span>
                    </label>
                  </div>
                )}
                <label>Statement Dates</label>

                <div className={styles.modalDateRow}>
                  <div
                    className={styles.inputGroup}
                    style={{ margin: 0, flex: 1 }}
                  >
                    <h3>📅 From Date: Optional</h3>
                    <input
                      type="date"
                      className={styles.datePill}
                      value={stmtFrom}
                      onChange={(e) => setStmtFrom(e.target.value)}
                    />
                  </div>

                  <div
                    className={styles.inputGroup}
                    style={{ margin: 0, flex: 1 }}
                  >
                    <h3>📅 To Date:</h3>
                    <input
                      type="date"
                      className={styles.datePill}
                      value={stmtTo}
                      onChange={(e) => setStmtTo(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* SELECTED DATE PREVIEW */}
              <div
                className={styles.infoText}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginTop: 6,
                  opacity: 0.9,
                }}
              >
                {stmtFrom && (
                  <span
                    style={{
                      border: "1px solid rgba(0,0,0,0.12)",
                      padding: "6px 10px",
                      borderRadius: "999px",
                    }}
                  >
                    ✅ From: {format(new Date(stmtFrom), "yyyy/MM/dd")}
                  </span>
                )}

                {stmtTo && (
                  <span
                    style={{
                      border: "1px solid rgba(0,0,0,0.12)",
                      padding: "6px 10px",
                      borderRadius: "999px",
                    }}
                  >
                    ✅ To: {format(new Date(stmtTo), "yyyy/MM/dd")}
                  </span>
                )}
              </div>
              <div className={styles.inputGroup}>
                <h3>File Type:</h3>
                <select
                  className={styles.selectPill}
                  value={stmtFormat}
                  onChange={(e) => setStmtFormat(e.target.value)}
                >
                  <option value="pdf">PDF</option>
                  <option value="excel">Excel</option>
                </select>
              </div>
            </div>

            {/* FOOTER BUTTONS */}
            <div className={styles.modalActions}>
              <button
                className={styles.modalBtnPrimary}
                onClick={() => handleStatement("download")}
                disabled={!!stmtLoadingType}
              >
                {stmtLoadingType?.startsWith("download")
                  ? "Downloading…"
                  : "Download Statement"}
              </button>

              <button
                className={styles.modalBtnSecondary}
                onClick={closeStatementModal}
              >
                Back
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
